// Mirror of backend `models/src/ai/bi-widgets.ts`.

/** The `bi_visualization` fence: the draft to render, and its title. */
export interface BiVisualizationWidgetPayload {
  draftId: string;
  /** Overrides the draft's own title in the bubble. */
  title?: string;
}

const kDraftId = /^bidraft_[0-9a-z]{26}$/;
const kMaxTitle = 100;

/**
 * The payload, or null for anything else — including a payload carrying
 * data beside the id, which is a model copying numbers into a fence.
 */
export function parseBiVisualizationWidgetPayload(
  value: unknown,
): BiVisualizationWidgetPayload | null {
  if (
    !value ||
    typeof value !== 'object' ||
    Array.isArray(value) ||
    Object.getPrototypeOf(value) !== Object.prototype
  )
    return null;
  const { draftId, title, ...rest } = value as Record<string, unknown>;
  if (
    Object.keys(rest).length > 0 ||
    typeof draftId !== 'string' ||
    !kDraftId.test(draftId)
  )
    return null;
  if (title === undefined) return { draftId };
  if (typeof title !== 'string') return null;
  const named = title.trim();
  if (!named || named.length > kMaxTitle) return null;
  return { draftId, title: named };
}

const kDashboardId = /^bidash_[0-9a-z]{26}$/;
const kChangeSetId = /^bichg_[0-9a-z]{26}$/;
const kMaxDraftsPerAdd = 20;
const kMaxDashboardName = 100;
export const kBiMaxDashboardCandidates = 3;

/** A dashboard offered in `bi_dashboard_choice`. */
export interface BiDashboardChoiceCandidate {
  dashboardId: string;
  name: string;
}

/**
 * The `bi_dashboard_choice` fence: where to add the drafts. Written by the
 * server in reply to an add tap; up to three suggestions, best first. The
 * client adds **New dashboard** and **Other…** (the full list).
 */
export interface BiDashboardChoiceWidgetPayload {
  draftIds: string[];
  candidates: BiDashboardChoiceCandidate[];
}

/** The `bi_dashboard_changes` fence: a proposed change set, by id. */
export interface BiDashboardChangesWidgetPayload {
  changeSetId: string;
}

function plainObject(value: unknown): Record<string, unknown> | null {
  if (
    !value ||
    typeof value !== 'object' ||
    Array.isArray(value) ||
    Object.getPrototypeOf(value) !== Object.prototype
  )
    return null;
  return value as Record<string, unknown>;
}

export function parseBiDashboardChoiceWidgetPayload(
  value: unknown,
): BiDashboardChoiceWidgetPayload | null {
  const raw = plainObject(value);
  if (!raw) return null;
  const { draftIds, candidates, ...rest } = raw;
  if (Object.keys(rest).length > 0) return null;
  if (
    !Array.isArray(draftIds) ||
    !draftIds.length ||
    draftIds.length > kMaxDraftsPerAdd ||
    !draftIds.every(id => typeof id === 'string' && kDraftId.test(id)) ||
    new Set(draftIds).size !== draftIds.length
  )
    return null;
  if (
    !Array.isArray(candidates) ||
    candidates.length > kBiMaxDashboardCandidates
  )
    return null;
  const parsed: BiDashboardChoiceCandidate[] = [];
  for (const entry of candidates) {
    const candidate = plainObject(entry);
    if (!candidate) return null;
    const { dashboardId, name, ...extra } = candidate;
    if (
      Object.keys(extra).length > 0 ||
      typeof dashboardId !== 'string' ||
      !kDashboardId.test(dashboardId) ||
      typeof name !== 'string' ||
      !name.trim() ||
      name.length > kMaxDashboardName
    )
      return null;
    parsed.push({ dashboardId, name });
  }
  if (new Set(parsed.map(c => c.dashboardId)).size !== parsed.length)
    return null;
  return { draftIds: draftIds as string[], candidates: parsed };
}

export function parseBiDashboardChangesWidgetPayload(
  value: unknown,
): BiDashboardChangesWidgetPayload | null {
  const raw = plainObject(value);
  if (!raw || Object.keys(raw).length !== 1) return null;
  const { changeSetId } = raw;
  if (typeof changeSetId !== 'string' || !kChangeSetId.test(changeSetId))
    return null;
  return { changeSetId };
}

const kUploadId = /^biupl_[0-9a-z]{26}$/;
const kDatasetId = /^bids_[0-9a-z]{26}$/;
/** Mirror of backend `kBiMaxProposals`. */
const kMaxProposals = 8;
/** Mirror of backend `kBiMaxDatasetChoices`. */
export const kBiMaxDatasetChoices = 5;

/** The `bi_upload` fence: a file upload the client follows by id. */
export interface BiUploadWidgetPayload {
  uploadId: string;
}

export function parseBiUploadWidgetPayload(
  value: unknown,
): BiUploadWidgetPayload | null {
  const raw = plainObject(value);
  if (!raw || Object.keys(raw).length !== 1) return null;
  const { uploadId } = raw;
  if (typeof uploadId !== 'string' || !kUploadId.test(uploadId)) return null;
  return { uploadId };
}

/** The `bi_insight_proposals` fence: proposal drafts, best first. */
export interface BiInsightProposalsWidgetPayload {
  draftIds: string[];
}

export function parseBiInsightProposalsWidgetPayload(
  value: unknown,
): BiInsightProposalsWidgetPayload | null {
  const raw = plainObject(value);
  if (!raw || Object.keys(raw).length !== 1) return null;
  const { draftIds } = raw;
  if (
    !Array.isArray(draftIds) ||
    !draftIds.length ||
    draftIds.length > kMaxProposals ||
    !draftIds.every(id => typeof id === 'string' && kDraftId.test(id)) ||
    new Set(draftIds).size !== draftIds.length
  )
    return null;
  return { draftIds: draftIds as string[] };
}

/**
 * The `bi_dataset_choice` fence: uploaded datasets, best match first; empty
 * when nothing matches, and the widget offers only the upload.
 */
export interface BiDatasetChoiceWidgetPayload {
  datasetIds: string[];
}

export function parseBiDatasetChoiceWidgetPayload(
  value: unknown,
): BiDatasetChoiceWidgetPayload | null {
  const raw = plainObject(value);
  if (!raw || Object.keys(raw).length !== 1) return null;
  const { datasetIds } = raw;
  if (
    !Array.isArray(datasetIds) ||
    datasetIds.length > kBiMaxDatasetChoices ||
    !datasetIds.every(id => typeof id === 'string' && kDatasetId.test(id)) ||
    new Set(datasetIds).size !== datasetIds.length
  )
    return null;
  return { datasetIds: datasetIds as string[] };
}
